import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowUpTrayIcon, 
  DocumentIcon, 
  XMarkIcon, 
  ExclamationCircleIcon,
  InformationCircleIcon,
  UserCircleIcon,
  PaintBrushIcon,
  DocumentDuplicateIcon,
  BoltIcon,
  CurrencyRupeeIcon,
  ArrowRightIcon,
  PhoneIcon,
  EnvelopeIcon
} from "@heroicons/react/24/outline";
import { uploadsAPI, publicAPI } from "../api";
import { getDeviceId, getActiveOrder, saveActiveOrder, buildTrackUrl } from "../utils/senderSession";

// Initial phase: keep the sender form minimal. Flip these on when needed.
const SHOW_CONTACT_FIELDS = false; // phone (WhatsApp) + email inputs
const SHOW_PRICING = false; // B&W/colour choice, per-page rates and estimated amounts

interface DocumentUploaderProps {
  shopId: string;
  shopName?: string;
  initialSenderInfo?: {
    name: string;
    phone: string;
    email: string;
  };
}

interface FileWithProgress {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
  documentId?: number;
  pageCount?: number;
  printColor: 'bw' | 'color';
  copies: number;
  estimatedCost: number;
  isUrgent: boolean;
  instructions: string;
}

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const ALLOWED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'text/plain',
];

export default function DocumentUploader({ shopId, shopName, initialSenderInfo }: DocumentUploaderProps) {
  const navigate = useNavigate();
  const [files, setFiles] = useState<FileWithProgress[]>([]);
  const [uploading, setUploading] = useState(false);
  const [shopRates, setShopRates] = useState({ bw: 2.0, color: 5.0 });
  const [overallProgress, setOverallProgress] = useState(0);
  const [activeOrder, setActiveOrder] = useState<string | null>(() => getActiveOrder(shopId));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setActiveOrder(getActiveOrder(shopId));
  }, [shopId]);

  // Load shop rates
  useEffect(() => {
    if (shopId) {
      publicAPI.getShopInfo(shopId).then(shop => {
        if (shop) {
          setShopRates({
            bw: (shop as any).price_per_page_bw || 2.0,
            color: (shop as any).price_per_page_color || 5.0
          });
        }
      }).catch(console.error);
    }
  }, [shopId]);

  const countPages = async (file: File): Promise<number> => {
    if (file.type === 'application/pdf') {
      try {
        const buffer = await file.arrayBuffer();
        const text = new TextDecoder('latin1').decode(buffer);
        // Method 1: Count /Type /Page objects (excluding /Pages)
        const pageMatches = text.match(/\/Type\s*\/Page\b/g);
        if (pageMatches && pageMatches.length > 0) {
          return pageMatches.length;
        }
        // Method 2: Match /Count in root Pages catalog
        const countMatch = text.match(/\/Count\s+(\d+)/);
        if (countMatch && countMatch[1]) {
          return parseInt(countMatch[1], 10) || 1;
        }
      } catch (e) {
        console.warn('Failed to count PDF pages', e);
      }
    }
    return 1;
  };

  const [senderInfo, setSenderInfo] = useState(() => {
    if (initialSenderInfo?.name) {
      return {
        name: initialSenderInfo.name,
        phone: initialSenderInfo.phone || '',
        email: initialSenderInfo.email || ''
      };
    }
    
    try {
      const cached = localStorage.getItem('lastSenderInfo');
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (e) {
      console.warn('Failed to load sender info', e);
    }    
    return { name: '', phone: '', email: '' };
  });

  useEffect(() => {
    if (senderInfo.name) {
      localStorage.setItem('lastSenderInfo', JSON.stringify(senderInfo));
    }
  }, [senderInfo]);

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `Too large (Max: 50MB)`;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Format not supported';
    }
    return null;
  };

  const addFiles = useCallback(async (newFiles: File[]) => {
    setErrorMessage(null);
    const validFiles: FileWithProgress[] = [];
    const errors: string[] = [];

    for (const file of newFiles) {
      const error = validateFile(file);
      if (error) {
        errors.push(`${file.name}: ${error}`);
      } else {
        const pageCount = await countPages(file);
        validFiles.push({
          file,
          progress: 0,
          status: 'pending',
          pageCount,
          printColor: 'bw',
          copies: 1,
          estimatedCost: pageCount * shopRates.bw,
          isUrgent: false,
          instructions: ''
        });
      }
    }

    if (errors.length > 0) {
      setErrorMessage(errors.join('\n'));
    }

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
    }
  }, [shopRates]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(Array.from(e.target.files));
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(Array.from(e.dataTransfer.files));
    }
  }, [addFiles]);

  const removeFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
    setErrorMessage(null);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const updateFileConfig = (index: number, updates: Partial<FileWithProgress>) => {
    setFiles(prev => prev.map((f, i) => {
      if (i !== index) return f;
      const updated = { ...f, ...updates };
      const rate = updated.printColor === 'color' ? shopRates.color : shopRates.bw;
      updated.estimatedCost = (updated.pageCount || 1) * updated.copies * rate;
      return updated;
    }));
  };

  const totalEstimatedCost = files.reduce((acc, f) => acc + (f.estimatedCost || 0), 0);
  const totalPages = files.reduce((acc, f) => acc + ((f.pageCount || 1) * f.copies), 0);

  const handleUpload = async () => {
    setErrorMessage(null);
    if (files.length === 0) return;
    if (!shopId) {
      setErrorMessage('Shop context lost. Please rescan.');
      return;
    }
    if (!senderInfo.name || senderInfo.name.trim() === '') {
      setErrorMessage('Please enter your name so the shopkeeper can identify your prints at the counter.');
      nameInputRef.current?.focus();
      return;
    }

    setUploading(true);
    setOverallProgress(0);

    try {
      const results = [];
      const deviceId = getDeviceId();
      // Join the sender's still-open order at this shop; the server starts a new one if it is closed
      let orderToken = getActiveOrder(shopId) || undefined;
      for (let i = 0; i < files.length; i++) {
        const fileItem = files[i];
        const metadata = {
          sender_name: senderInfo.name.trim(),
          sender_phone: SHOW_CONTACT_FIELDS ? senderInfo.phone || undefined : undefined,
          sender_email: SHOW_CONTACT_FIELDS ? senderInfo.email || undefined : undefined,
          print_copies: fileItem.copies,
          print_color: fileItem.printColor,
          is_urgent: fileItem.isUrgent,
          special_instructions: fileItem.instructions,
          estimated_cost: fileItem.estimatedCost,
          order_token: orderToken,
          device_id: deviceId
        };

        const result = await uploadsAPI.uploadFile(
          fileItem.file,
          shopId,
          metadata,
          (progress) => {
            setFiles(prev => prev.map((f, idx) => 
              idx === i ? { ...f, progress, status: progress === 100 ? 'success' : 'uploading' } : f
            ));
          }
        );
        results.push(result);
        if (result.order_token) orderToken = result.order_token;
        setOverallProgress(Math.round(((i + 1) / files.length) * 100));
      }

      setFiles(prev => prev.map(file => ({ ...file, status: 'success', progress: 100 })));

      // Go straight to live tracking for the whole order
      if (orderToken) {
        saveActiveOrder(shopId, orderToken);
        navigate(buildTrackUrl(orderToken, shopId, shopName));
      } else if (results.length > 0 && results[0].document_id) {
        // Older backend without order tokens
        const code = `RC${results[0].document_id.toString().padStart(6, '0')}`;
        navigate(`/track?code=${code}&shop=${encodeURIComponent(shopId)}&name=${encodeURIComponent(shopName || '')}`);
      }

    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Upload failed';
      setErrorMessage(msg);
      setFiles(prev => prev.map(file =>
        file.status === 'uploading' ? { ...file, status: 'error', error: msg } : file
      ));
    } finally {
      setUploading(false);
    }
  };

  // --- Main Upload View ---
  return (
    <div className="space-y-6">
      {activeOrder && !uploading && (
        <button
          onClick={() => navigate(buildTrackUrl(activeOrder, shopId, shopName))}
          className="w-full bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-4 flex items-center justify-between text-left hover:bg-emerald-100 transition-all active:scale-[0.99]"
        >
          <div>
            <p className="text-xs font-black text-emerald-800 uppercase tracking-wider">You have an order here</p>
            <p className="text-[11px] font-medium text-emerald-700 mt-0.5">Check its status. New files you send will be added to it.</p>
          </div>
          <ArrowRightIcon className="w-5 h-5 text-emerald-700 shrink-0" />
        </button>
      )}

      {errorMessage && (
        <div className="bg-red-50 border-2 border-red-200 p-4 rounded-2xl flex items-start space-x-3 animate-slideUp">
          <ExclamationCircleIcon className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-xs font-black text-red-900 uppercase">Attention Required</h3>
            <p className="text-red-700 text-xs font-bold mt-0.5 whitespace-pre-line leading-relaxed">{errorMessage}</p>
          </div>
          <button onClick={() => setErrorMessage(null)} className="p-1 text-red-400 hover:text-red-600">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* 1. SENDER DETAILS CARD */}
      <div className="bg-white rounded-3xl p-5 sm:p-6 shadow-sm border-2 border-slate-200 relative overflow-hidden">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <UserCircleIcon className="w-5 h-5 text-blue-600" />
            <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest">
              Sender Details
            </h3>
          </div>
          {senderInfo.name.trim() ? (
            <span className="inline-flex items-center text-[10px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-100">
              ✓ Ready
            </span>
          ) : (
            <span className="text-[10px] font-black uppercase tracking-wider text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-100">
              * Name Required
            </span>
          )}
        </div>

        <div className="space-y-3.5">
          {/* Full Name Input */}
          <div>
            <label className="block text-[11px] font-black uppercase tracking-wider text-slate-700 mb-1.5">
              Your Full Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input 
                ref={nameInputRef}
                type="text" 
                placeholder="e.g. Rahul Sharma"
                value={senderInfo.name}
                onChange={(e) => setSenderInfo({ ...senderInfo, name: e.target.value })}
                className="w-full bg-slate-50 focus:bg-white rounded-2xl px-4 py-3.5 text-sm font-bold text-slate-900 placeholder:text-slate-400 border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all"
                required
              />
            </div>
            <p className="text-[10px] text-slate-400 mt-1 font-medium">
              Used by the shopkeeper to call your name when prints are ready.
            </p>
          </div>

          {/* Optional Phone & Email */}
          {SHOW_CONTACT_FIELDS && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">
                Phone Number <span className="text-slate-400 font-normal">(Optional)</span>
              </label>
              <div className="relative flex items-center">
                <PhoneIcon className="w-4 h-4 text-slate-400 absolute left-3.5 pointer-events-none" />
                <input 
                  type="tel" 
                  placeholder="For WhatsApp updates"
                  value={senderInfo.phone}
                  onChange={(e) => setSenderInfo({ ...senderInfo, phone: e.target.value })}
                  className="w-full bg-slate-50 focus:bg-white rounded-xl pl-10 pr-3 py-2.5 text-xs font-bold text-slate-900 placeholder:text-slate-400 border border-slate-200 focus:border-blue-500 outline-none transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">
                Email <span className="text-slate-400 font-normal">(Optional)</span>
              </label>
              <div className="relative flex items-center">
                <EnvelopeIcon className="w-4 h-4 text-slate-400 absolute left-3.5 pointer-events-none" />
                <input 
                  type="email" 
                  placeholder="For receipt copy"
                  value={senderInfo.email}
                  onChange={(e) => setSenderInfo({ ...senderInfo, email: e.target.value })}
                  className="w-full bg-slate-50 focus:bg-white rounded-xl pl-10 pr-3 py-2.5 text-xs font-bold text-slate-900 placeholder:text-slate-400 border border-slate-200 focus:border-blue-500 outline-none transition-all"
                />
              </div>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* 2. FILE DROP ZONE */}
      <div className="relative group">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
          onChange={handleFileSelect}
          className="hidden"
          id="file-upload"
          disabled={uploading}
        />

        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDrop}
          className={`
            relative border-3 border-dashed rounded-[2.5rem] p-8 text-center transition-all duration-300 overflow-hidden
            ${dragActive
              ? 'border-blue-500 bg-blue-50/80 scale-[0.99]'
              : 'border-slate-200 bg-white hover:border-blue-400 shadow-sm'
            }
          `}
        >
          <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center">
            <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-4 text-blue-600 group-hover:scale-105 transition-transform shadow-inner">
              <ArrowUpTrayIcon className={`w-8 h-8 ${dragActive ? 'animate-bounce' : ''}`} />
            </div>
            <h3 className="text-lg font-black text-slate-900 tracking-tight mb-1">
              {dragActive ? 'Drop documents here' : 'Tap to Add Documents'}
            </h3>
            <p className="text-slate-500 text-xs font-medium mb-5">
              Supports PDF, Word, Images (Max 50MB)
            </p>
            <span className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl text-xs font-black uppercase tracking-widest shadow-lg shadow-blue-500/20 active:scale-95 transition-all">
              Choose Files
            </span>
          </label>
        </div>
      </div>

      {/* 3. SELECTED FILES LIST & CONFIGURATION */}
      {files.length > 0 && (
        <div className="space-y-4 animate-slideUp">
          <div className="flex items-center justify-between px-2">
            <h4 className="text-xs font-black text-slate-600 uppercase tracking-widest">
              Selected Documents ({files.length})
            </h4>
            {!uploading && (
              <button 
                onClick={() => setFiles([])} 
                className="text-[11px] font-black text-red-500 hover:text-red-700 uppercase tracking-wider"
              >
                Clear All
              </button>
            )}
          </div>

          <div className="space-y-3">
            {files.map((fileItem, index) => (
              <div key={index} className="bg-white rounded-3xl p-5 shadow-sm border border-slate-200/80 animate-fadeIn">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3.5 min-w-0">
                    <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${
                      fileItem.status === 'success' ? 'bg-emerald-100 text-emerald-600' : 
                      fileItem.status === 'error' ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-600'
                    }`}>
                      <DocumentIcon className="w-6 h-6" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-black text-slate-900 truncate pr-2">{fileItem.file.name}</p>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">
                        {formatFileSize(fileItem.file.size)} • {fileItem.pageCount || 1} {fileItem.pageCount === 1 ? 'Page' : 'Pages'}
                      </p>
                    </div>
                  </div>
                  {!uploading && (
                    <button 
                      onClick={() => removeFile(index)} 
                      className="p-1.5 text-slate-300 hover:text-red-500 transition-colors rounded-lg"
                      aria-label="Remove file"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  )}
                </div>

                {!uploading && fileItem.status !== 'success' && (
                  <div className="space-y-3 pt-2">
                    <div className={`grid gap-2.5 ${SHOW_PRICING ? 'grid-cols-2' : 'grid-cols-1'}`}>
                      {/* B&W vs Color selector */}
                      {SHOW_PRICING && (
                      <div className="bg-slate-50 p-2.5 rounded-2xl border border-slate-100 flex flex-col">
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">
                          Print Color
                        </span>
                        <div className="flex items-center space-x-1.5">
                          <PaintBrushIcon className="w-3.5 h-3.5 text-slate-400" />
                          <select 
                            className="bg-transparent text-[11px] font-black uppercase tracking-tight outline-none w-full text-slate-800"
                            value={fileItem.printColor}
                            onChange={(e) => updateFileConfig(index, { printColor: e.target.value as 'bw' | 'color' })}
                          >
                            <option value="bw">B&W (₹{shopRates.bw}/page)</option>
                            <option value="color">Color (₹{shopRates.color}/page)</option>
                          </select>
                        </div>
                      </div>
                      )}

                      {/* Copies selector */}
                      <div className="bg-slate-50 p-2.5 rounded-2xl border border-slate-100 flex flex-col">
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">
                          Copies
                        </span>
                        <div className="flex items-center space-x-1.5">
                          <DocumentDuplicateIcon className="w-3.5 h-3.5 text-slate-400" />
                          <input 
                            type="number" 
                            min="1" 
                            max="999"
                            className="bg-transparent text-[11px] font-black uppercase tracking-tight outline-none w-full text-slate-800"
                            value={fileItem.copies}
                            onChange={(e) => updateFileConfig(index, { copies: Math.max(1, parseInt(e.target.value) || 1) })}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Urgent Toggle & Estimated Price */}
                    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                      <button 
                        onClick={() => updateFileConfig(index, { isUrgent: !fileItem.isUrgent })}
                        className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl transition-all text-xs font-black uppercase tracking-wider ${
                          fileItem.isUrgent 
                            ? 'bg-amber-100 text-amber-700 border border-amber-200' 
                            : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                        }`}
                      >
                        <BoltIcon className="w-3.5 h-3.5" />
                        <span>Urgent Job</span>
                      </button>
                      {SHOW_PRICING && (
                      <div className="flex items-center space-x-1">
                        <CurrencyRupeeIcon className="w-4 h-4 text-blue-600" />
                        <span className="text-base font-black text-slate-900">₹{fileItem.estimatedCost.toFixed(0)}</span>
                      </div>
                      )}
                    </div>
                  </div>
                )}

                {fileItem.status === 'uploading' && (
                  <div className="mt-3 space-y-1.5">
                    <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-600 transition-all duration-300" style={{ width: `${fileItem.progress}%` }} />
                    </div>
                    <p className="text-[9px] font-black text-blue-600 uppercase tracking-widest text-right">{fileItem.progress}% Uploaded</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Order Summary Box */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-3xl p-5 shadow-xl flex items-center justify-between">
            <div>
              <p className="text-[10px] font-black text-blue-300 uppercase tracking-widest mb-0.5">Order Summary</p>
              <p className="text-xs font-bold text-slate-300">
                {files.length} {files.length === 1 ? 'Doc' : 'Docs'} • {totalPages} Total Pages
              </p>
            </div>
            {SHOW_PRICING && (
            <div className="text-right">
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Est. Amount</p>
              <p className="text-2xl font-black text-white">₹{totalEstimatedCost.toFixed(0)}</p>
            </div>
            )}
          </div>
        </div>
      )}

      {/* 4. OVERALL PROGRESS BAR */}
      {uploading && (
        <div className="bg-white rounded-3xl p-6 shadow-xl border border-blue-200 animate-slideUp">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest">Transmitting to Counter...</h4>
            <span className="text-sm font-black text-blue-600">{overallProgress}%</span>
          </div>
          <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden mb-2">
            <div 
              className="h-full bg-gradient-to-r from-blue-600 to-indigo-600 transition-all duration-500" 
              style={{ width: `${overallProgress}%` }} 
            />
          </div>
          <p className="text-[10px] font-bold text-slate-400 text-center animate-pulse">
            Keep this screen open while documents are uploaded
          </p>
        </div>
      )}

      {/* 5. ACTION SEND BUTTON */}
      <button
        onClick={handleUpload}
        disabled={files.length === 0 || uploading}
        className={`w-full py-5 rounded-[2rem] font-black text-base transition-all shadow-2xl uppercase tracking-[0.15em] flex items-center justify-center group ${
          files.length === 0 || uploading
            ? 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none'
            : 'bg-blue-600 text-white hover:bg-blue-700 shadow-blue-500/25 active:scale-95'
        }`}
      >
        {uploading ? (
          <div className="flex items-center space-x-3">
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            <span className="text-sm">Transmitting Files...</span>
          </div>
        ) : (
          <div className="flex items-center space-x-2">
            <span>Send to {shopName || 'Shop'}</span>
            <ArrowUpTrayIcon className="w-5 h-5 group-hover:-translate-y-0.5 transition-transform" />
          </div>
        )}
      </button>

      {/* 6. PRIVACY FOOTER NOTE */}
      <div className="bg-white rounded-3xl p-4 border border-slate-100 flex items-start space-x-3.5 shadow-sm">
        <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center shrink-0 text-blue-600">
          <InformationCircleIcon className="w-5 h-5" />
        </div>
        <div>
          <h4 className="text-[10px] font-black text-slate-800 uppercase tracking-widest mb-0.5">Privacy Notice</h4>
          <p className="text-[11px] font-medium text-slate-500 leading-relaxed">
            Your files are securely encrypted and automatically wiped from shop records once printed.
          </p>
        </div>
      </div>
    </div>
  );
}
